import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  fetchInterviewHistory,
  scheduleInterview,
  rescheduleInterview,
  startInterview,
  cancelInterview,
  noShowInterview,
  completeInterview,
} from './interview.thunk';

import type { Interview, InterviewState } from '@/modules/hrPortal/types/interview.types';

const initialState: InterviewState = {
  interviews: [],
  loading: false,
  error: null,
  scheduling: false,
  rescheduling: false,
  updating: false,
};

const interviewSlice = createSlice({
  name: 'interview',
  initialState,
  reducers: {
    clearInterviewState: (state) => {
      state.interviews = [];
      state.loading = false;
      state.error = null;
      state.scheduling = false;
      state.rescheduling = false;
      state.updating = false;
    },

    clearInterviewError: (state) => {
      state.error = null;
    },

    setInterviews: (state, action: PayloadAction<Interview[]>) => {
      state.interviews = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchInterviewHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchInterviewHistory.fulfilled,
        (state, action) => {
          state.loading = false;
          state.interviews = action.payload;
        }
      )
      .addCase(
        fetchInterviewHistory.rejected,
        (state, action) => {
          state.loading = false;
          state.error =
            action.payload || 'Unable to fetch interview history.';
        }
      )

      .addCase(scheduleInterview.pending, (state) => {
        state.scheduling = true;
        state.error = null;
      })
      .addCase(scheduleInterview.fulfilled, (state, action) => {
        state.scheduling = false;

        const existingIndex = state.interviews.findIndex(
          (interview) => interview.id === action.payload.id
        );

        if (existingIndex >= 0) {
          state.interviews[existingIndex] = action.payload;
        } else {
          state.interviews.push(action.payload);
        }
      })
      .addCase(scheduleInterview.rejected, (state, action) => {
        state.scheduling = false;
        state.error =
          action.payload || 'Unable to schedule interview.';
      })

      .addCase(rescheduleInterview.pending, (state) => {
        state.rescheduling = true;
        state.error = null;
      })
      .addCase(rescheduleInterview.fulfilled, (state, action) => {
        state.rescheduling = false;

        const index = state.interviews.findIndex(
          (interview) => interview.id === action.payload.id
        );

        if (index >= 0) {
          state.interviews[index] = action.payload;
        }
      })
      .addCase(rescheduleInterview.rejected, (state, action) => {
        state.rescheduling = false;
        state.error =
          action.payload || 'Unable to reschedule interview.';
      })

      .addCase(startInterview.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(startInterview.fulfilled, (state, action) => {
        state.updating = false;

        const index = state.interviews.findIndex(
          (interview) => interview.id === action.payload.id
        );

        if (index >= 0) {
          state.interviews[index] = action.payload;
        }
      })
      .addCase(startInterview.rejected, (state, action) => {
        state.updating = false;
        state.error =
          action.payload || 'Unable to start interview.';
      })

      .addCase(cancelInterview.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(cancelInterview.fulfilled, (state, action) => {
        state.updating = false;

        const index = state.interviews.findIndex(
          (interview) => interview.id === action.payload.id
        );

        if (index >= 0) {
          state.interviews[index] = action.payload;
        }
      })
      .addCase(cancelInterview.rejected, (state, action) => {
        state.updating = false;
        state.error =
          action.payload || 'Unable to cancel interview.';
      })

      .addCase(noShowInterview.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(noShowInterview.fulfilled, (state, action) => {
        state.updating = false;

        const index = state.interviews.findIndex(
          (interview) => interview.id === action.payload.id
        );

        if (index >= 0) {
          state.interviews[index] = action.payload;
        }
      })
      .addCase(noShowInterview.rejected, (state, action) => {
        state.updating = false;
        state.error =
          action.payload || 'Unable to mark interview as no-show.';
      })

      .addCase(completeInterview.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(completeInterview.fulfilled, (state, action) => {
        state.updating = false;

        const index = state.interviews.findIndex(
          (interview) => interview.id === action.payload.id
        );

        if (index >= 0) {
          state.interviews[index] = action.payload;
        }
      })
      .addCase(completeInterview.rejected, (state, action) => {
        state.updating = false;
        state.error =
          action.payload || 'Unable to complete interview.';
      });
  },
});

export const {
  clearInterviewState,
  clearInterviewError,
  setInterviews,
} = interviewSlice.actions;

export default interviewSlice.reducer;